import { APPARENT_AGE_YEARS, SAME_AGE_TOLERANCE, } from './constants.js';
import { formatFuzzyDate, fuzzyDateYearRange, yearSpanLabel, } from './fuzzy-date.js';
/**
 * Wann wurde dieses Bild aufgenommen? – geschlossen aus den Menschen darauf.
 *
 * Die Umkehrung von {@link photoDateConflict}: dort wird ein bekanntes
 * Aufnahmedatum gegen Lebensdaten geprüft, hier wird aus den Lebensdaten das
 * Aufnahmedatum eingegrenzt. Es ist dieselbe Ungleichung, nur nach der anderen
 * Seite aufgelöst – und deshalb genauso belastbar wie die Jahre, die im Archiv
 * stehen.
 *
 * ## Was gerechnet wird, und was ausdrücklich nicht
 *
 * Gerechnet wird die **Schnittmenge der Lebensfenster**: das Bild kann nicht
 * vor der Geburt des Jüngsten entstanden sein und nicht nach dem Tod des zuerst
 * Verstorbenen. Das Ergebnis ist keine Vermutung, sondern eine Grenze – wer
 * 1955 geboren ist, ist auf keinem Bild von 1950.
 *
 * Dazu kommt eine zweite Schicht: **wie alt jemand auf dem Bild aussieht**.
 * Diese Auskunft rechnet niemand aus, sie wird gefragt – ein Blick auf das
 * Gesicht, eine Stufe angeklickt (siehe `APPARENT_AGES`). Aus vierzig Jahren
 * Fenster werden damit acht, und bei einem Säugling drei. Genau hier wäre eine
 * Maschine schwach (Kinder, historische Kleidung, schlechte Scans) und ein
 * Mensch stark, der die Fotos kennt.
 *
 * Dazu gehört ein zweiter, stärkerer Fall derselben Art: der **Vergleich mit
 * einem datierten Bild derselben Person**. „Hier ist sie jünger als auf dem
 * Bild von 1968" heißt „dieses Bild ist von 1968 oder früher" – und das ohne
 * jedes Geburtsjahr, ohne jede Altersschätzung. Für Personen, über die das
 * Archiv nur Bilder hat und keine Daten, ist das die einzige Grenze, die
 * überhaupt zu holen ist.
 *
 * Beide Schichten werden getrennt gerechnet und dann geschnitten. Der
 * Unterschied zählt, wenn sie sich widersprechen: zwei Lebensdaten, die nicht
 * zusammenpassen, sind ein Fehler in den Daten; ein menschliches Urteil, das
 * nicht zu den Lebensdaten passt, ist ein Fehlurteil. Im zweiten Fall bleibt
 * die harte Grenze stehen und das Urteil wird gemeldet, nicht eingerechnet.
 *
 * ## Warum nichts davon gespeichert wird
 *
 * Die Schätzung wird bei jedem Aufruf neu gerechnet und nirgends abgelegt.
 * Damit ist sie immer auf dem Stand des Archivs: wer heute ein Geburtsjahr
 * nachträgt oder eine weitere Person markiert, sieht am Bild sofort das engere
 * Fenster – ohne Auffrischungslauf, ohne veralteten Zwischenspeicher. Nur die
 * *Übernahme* ins Aufnahmedatum ist ein Schreibvorgang, und die trifft ein
 * Mensch (siehe `media.takenAtSource`).
 */
/**
 * Höchstalter, das als obere Grenze angenommen wird, wenn kein Sterbejahr
 * bekannt ist.
 *
 * Die einzige Annahme in dieser Rechnung, und eine bewusst großzügige: sie
 * soll ein Fenster erzeugen, das sicher stimmt, und nicht das engste
 * wahrscheinliche. Ein Bild einer 1880 geborenen Person ist damit „vor 1980" –
 * schwach, aber wahr.
 */
export const ASSUMED_MAX_AGE = 100;
function boundLabel(kind, name, year, birthYear) {
    switch (kind) {
        case 'birth':
            return `${name} ist ${birthYear} geboren`;
        case 'death':
            return `${name} ist ${year} gestorben`;
        case 'lifespan':
            return `${name} ist ${birthYear} geboren – mehr als ${ASSUMED_MAX_AGE} Jahre werden nicht angenommen`;
        case 'today':
            return 'später als heute kann kein Bild aufgenommen sein';
        case 'appraisal':
            // Ohne Geschlechtsform und mit den Zahlen, die die Grenze tatsächlich
            // setzen: „wirkt wie ein Kind" liest sich schöner, sagt aber nicht, warum
            // das Fenster dort endet.
            return `${name} wirkt im Bild wie ${year}–${birthYear} Jahre alt`;
        case 'comparison':
            // Diese Grenze bekommt ihre Beschriftung beim Bauen mitgegeben – sie
            // braucht den Namen des Vergleichsbildes, und der passt in diese
            // Signatur nicht. Siehe `comparisonLabel`.
            return name;
    }
}
/**
 * „Anita ist hier jünger als auf „Weihnachten" (um 1970)".
 *
 * Öffentlich, weil derselbe Satz an zwei Stellen gebraucht wird: in der
 * Begründung des Zeitraums und in der Liste der gegebenen Antworten. Zwei
 * Formulierungen für dieselbe Aussage wären genau dort verwirrend, wo jemand
 * einen Widerspruch auflösen will.
 */
export function describeComparison(name, evidence) {
    const how = evidence.verdict === 'younger'
        ? 'jünger'
        : evidence.verdict === 'older'
            ? 'älter'
            : 'etwa gleich alt';
    const than = evidence.verdict === 'same' ? 'wie' : 'als';
    const when = formatFuzzyDate(evidence.referenceDate, '');
    return `${name} ist hier ${how} ${than} auf „${evidence.referenceLabel}"${when ? ` (${when})` : ''}`;
}
/** Die Beschriftung einer Einschätzungsgrenze – beide Grenzen tragen dieselbe. */
function appraisalLabel(name, age) {
    const span = APPARENT_AGE_YEARS[age];
    return boundLabel('appraisal', name, span.from, span.to);
}
/**
 * Jahresgrenzen als unscharfes Datum – „zwischen 1938 und 1971“, „nach 1955“.
 *
 * `text` bleibt bewusst leer. Das Feld ist die *Originaleingabe* eines
 * Menschen: das Eingabefeld zeigt es unverändert an und liest es beim nächsten
 * Öffnen wieder ein. Eine Begründung wie „aus den Personen im Bild geschätzt"
 * stand damit als Datum im Feld und wurde als unlesbar gemeldet – der Fehler
 * fiel beim Ausprobieren auf. Woher der Wert kommt, gehört nicht ins Datum,
 * sondern in `media.takenAtSource`; angezeigt wird dann der Zeitraum selbst.
 */
export function estimateAsFuzzyDate(from, to) {
    const jan = (year) => `${String(year).padStart(4, '0')}-01-01`;
    const dec = (year) => `${String(year).padStart(4, '0')}-12-31`;
    const text = null;
    if (from !== null && to !== null) {
        return from === to
            ? { qualifier: 'exact', precision: 'year', start: jan(from), end: dec(to), text }
            : { qualifier: 'between', precision: 'year', start: jan(from), end: dec(to), text };
    }
    if (from !== null) {
        return { qualifier: 'after', precision: 'year', start: jan(from), end: null, text };
    }
    if (to !== null) {
        return { qualifier: 'before', precision: 'year', start: null, end: dec(to), text };
    }
    return { qualifier: 'unknown', precision: 'year', start: null, end: null, text: null };
}
/**
 * Das Aufnahmefenster aus den Personen eines Bildes.
 *
 * `null`, wenn sich nichts sagen lässt – kein Mensch auf dem Bild hat ein Jahr.
 * Dann soll die Oberfläche auch nichts behaupten; „irgendwann zwischen 0 und
 * heute" ist keine Auskunft, sondern Lärm.
 *
 * Übergeben wird die Personenliste, wie sie in der Oberfläche vorliegt:
 * `null`-Einträge (unbenannte Markierungen) und Doppelungen (zwei Markierungen
 * derselben Person) fallen heraus. Verschleierte Personen dürfen mit: ihr
 * Geburtsjahr ist aufs Jahrzehnt abgerundet, das verschiebt die untere Grenze
 * nur nach *früher* – das Fenster wird dadurch weiter, nie falsch eng.
 */
export function estimatePhotoDate(entries, opts = {}) {
    const currentYear = opts.today ?? new Date().getUTCFullYear();
    // Entdoppeln über die Personenkennung. Die erste Nennung gewinnt: die
    // Markierungen stehen vor den bloßen Verknüpfungen, und nur sie tragen eine
    // Alterseinschätzung.
    const seen = new Set();
    const people = [];
    for (const entry of entries) {
        if (!entry?.person || seen.has(entry.person.id))
            continue;
        seen.add(entry.person.id);
        people.push(entry);
    }
    // ── Schicht 1: die Lebensdaten ─────────────────────────────────────────
    let earliest = null;
    let latest = null;
    const missing = [];
    const appraisable = [];
    let used = 0;
    let appraised = 0;
    for (const { person } of people) {
        if (person.birthYear === null && person.deathYear === null) {
            missing.push({ id: person.id, displayName: person.displayName });
            continue;
        }
        used += 1;
        const name = person.displayName;
        // Untere Grenze: das Bild ist nach der Geburt entstanden. Der *späteste*
        // Geburtsjahrgang auf dem Bild bestimmt sie – meist ein Kind, und das ist
        // der Grund, warum Kinderbilder die engsten Fenster ergeben.
        if (person.birthYear !== null && (earliest === null || person.birthYear > earliest.year)) {
            earliest = {
                kind: 'birth',
                year: person.birthYear,
                personId: person.id,
                personName: name,
                label: boundLabel('birth', name, person.birthYear, person.birthYear),
                assumed: false,
            };
        }
        // Obere Grenze: das Bild ist vor dem Tod entstanden. Ohne Sterbejahr bleibt
        // nur die Annahme über die Lebenslänge – schwächer, aber immer noch eine
        // Grenze, und sie wird als Annahme gekennzeichnet.
        const upper = person.deathYear !== null
            ? {
                kind: 'death',
                year: person.deathYear,
                personId: person.id,
                personName: name,
                label: boundLabel('death', name, person.deathYear, person.birthYear ?? person.deathYear),
                assumed: false,
            }
            : person.birthYear !== null
                ? {
                    kind: 'lifespan',
                    year: person.birthYear + ASSUMED_MAX_AGE,
                    personId: person.id,
                    personName: name,
                    label: boundLabel('lifespan', name, person.birthYear + ASSUMED_MAX_AGE, person.birthYear),
                    assumed: true,
                }
                : null;
        if (upper && (latest === null || upper.year < latest.year))
            latest = upper;
    }
    // Eine obere Grenze jenseits des laufenden Jahres sagt nichts – dann ist
    // „heute" die Grenze, und die gilt für jedes Bild. Als Grenze *gemeldet*
    // wird sie nur, wenn keine bessere da ist; sonst stünde an jedem Bild einer
    // lebenden Person „spätestens heute", und das weiß man schon.
    if (latest !== null && latest.year > currentYear) {
        latest = latest.assumed
            ? {
                kind: 'today',
                year: currentYear,
                personId: null,
                personName: null,
                label: boundLabel('today', '', currentYear, currentYear),
                assumed: true,
            }
            : latest;
    }
    const hardFrom = earliest?.year ?? null;
    // Die „heute"-Grenze ist wahr, aber als Fensterende unbrauchbar: sie macht
    // aus „frühestens 1990" ein „1990–2026" und damit aus einer offenen Angabe
    // eine scheinbar geschlossene. Offen bleibt offen.
    const hardTo = latest && latest.kind === 'today' ? null : (latest?.year ?? null);
    const impossible = hardFrom !== null && hardTo !== null && hardFrom > hardTo;
    // ── Schicht 2: was Menschen am Bild sehen ──────────────────────────────
    //
    // Zwei Arten von Urteil, eine Schicht: die Altersstufe („wirkt wie ein
    // Kind") und der Vergleich mit einem datierten Bild („hier jünger als
    // 1968"). Beide sind Beobachtungen und beide können daneben liegen – deshalb
    // werden sie zusammen gerechnet, zusammen geprüft und im Zweifel zusammen
    // verworfen.
    //
    // Der Unterschied zur ersten Schicht ist nicht die Verlässlichkeit, sondern
    // die Art des Fehlers: eine falsche Jahreszahl im Stammbaum ist ein Fehler in
    // den Daten und muss dort behoben werden. Ein Fehlurteil am Bild ist ein
    // Fehlurteil und wird zurückgenommen.
    // Als Objekt und nicht als zwei `let`: die beiden werden in Hilfsfunktionen
    // gesetzt, und TypeScript verfolgt Zuweisungen aus einer Closure nicht – die
    // Variablen gälten danach als `null` und jede Prüfung als toter Zweig.
    const judge = {
        from: null,
        to: null,
    };
    let compared = 0;
    /** Die engere der beiden Grenzen gewinnt – Seite für Seite. */
    const offerLower = (bound) => {
        if (judge.from === null || bound.year > judge.from.year)
            judge.from = bound;
    };
    const offerUpper = (bound) => {
        if (judge.to === null || bound.year < judge.to.year)
            judge.to = bound;
    };
    for (const { person, apparentAge, comparisons } of people) {
        const name = person.displayName;
        // ── Die Altersstufe – braucht ein Geburtsjahr ────────────────────────
        if (person.birthYear !== null) {
            if (apparentAge) {
                appraised += 1;
                const span = APPARENT_AGE_YEARS[apparentAge];
                const label = appraisalLabel(name, apparentAge);
                const make = (year) => ({
                    kind: 'appraisal',
                    year,
                    personId: person.id,
                    personName: name,
                    label,
                    assumed: true,
                });
                offerLower(make(person.birthYear + span.from));
                offerUpper(make(person.birthYear + span.to));
            }
            else {
                // Nur wer ein Geburtsjahr hat, kann mit einer Altersstufe etwas
                // ausrichten – bei allen anderen wäre die Frage vergeudet.
                appraisable.push({ id: person.id, displayName: person.displayName });
            }
        }
        // ── Der Vergleich – braucht kein Geburtsjahr ─────────────────────────
        for (const evidence of comparisons ?? []) {
            const range = fuzzyDateYearRange(evidence.referenceDate);
            // Von jeder Seite die *sichere* Grenze: das späteste mögliche Jahr des
            // Vergleichsbildes begrenzt nach oben, das früheste nach unten. Ist die
            // betreffende Seite offen („nach 1918"), taugt der Vergleich in diese
            // Richtung nicht – dann bleibt sie offen.
            const latestPossible = range.to;
            const earliestPossible = range.from;
            if (earliestPossible === null && latestPossible === null)
                continue;
            compared += 1;
            const label = describeComparison(name, evidence);
            const make = (year) => ({
                kind: 'comparison',
                year,
                personId: person.id,
                personName: name,
                label,
                // Ein Urteil, keine Urkunde – aber das belastbarste, das ein Betrachter
                // abgeben kann.
                assumed: true,
            });
            // „Jünger" heißt „früher oder im selben Jahr": zwischen Dezember und
            // Januar liegt ein Jahreswechsel und kein Altersunterschied. Die Grenze
            // ist deshalb einschließlich.
            if (evidence.verdict === 'younger' && latestPossible !== null) {
                offerUpper(make(latestPossible));
            }
            if (evidence.verdict === 'older' && earliestPossible !== null) {
                offerLower(make(earliestPossible));
            }
            if (evidence.verdict === 'same') {
                if (earliestPossible !== null)
                    offerLower(make(earliestPossible - SAME_AGE_TOLERANCE));
                if (latestPossible !== null)
                    offerUpper(make(latestPossible + SAME_AGE_TOLERANCE));
            }
        }
    }
    // Nichts, aus dem sich etwas schließen ließe – auch keine Urteile. Erst hier
    // aufgeben und nicht schon nach der ersten Schicht: ein Vergleich mit einem
    // datierten Bild trägt ganz allein, ohne jedes Lebensdatum.
    if (earliest === null && latest === null && judge.from === null && judge.to === null)
        return null;
    // ── Die beiden Schichten zusammenführen ────────────────────────────────
    let judgementConflict = null;
    let from = hardFrom;
    let to = hardTo;
    const lower = judge.from;
    const upper = judge.to;
    if (!impossible && (lower !== null || upper !== null)) {
        const hardBecause = [earliest?.label, latest && latest.kind !== 'today' ? latest.label : null]
            .filter(Boolean)
            .join(' und ');
        if (lower !== null && upper !== null && lower.year > upper.year) {
            judgementConflict =
                `Die Angaben am Bild widersprechen sich: ${lower.label}, ${upper.label}. ` +
                    'Solange das so steht, gilt nur, was die Lebensdaten hergeben.';
        }
        else if ((lower !== null && hardTo !== null && lower.year > hardTo) ||
            (upper !== null && hardFrom !== null && upper.year < hardFrom)) {
            judgementConflict =
                `Das passt nicht zu den Lebensdaten: ${(lower ?? upper).label}, aber ${hardBecause}. ` +
                    'Entweder ist das Urteil daneben oder ein Jahr im Stammbaum stimmt nicht.';
        }
        else {
            if (lower !== null && (hardFrom === null || lower.year > hardFrom)) {
                earliest = lower;
                from = lower.year;
            }
            if (upper !== null && (hardTo === null || upper.year < hardTo)) {
                latest = upper;
                to = upper.year;
            }
        }
    }
    // Bei einem Widerspruch wäre die Breite negativ – eine Zahl, die nirgends
    // etwas heißt. Dann sagt `impossible` alles, und die Breite bleibt leer.
    const span = from !== null && to !== null && from <= to ? to - from : null;
    const label = impossible ? 'widersprüchlich' : yearSpanLabel(from, to);
    // Zwei Halbsätze, aber nicht zweimal derselbe.
    //
    // Drei Fälle laufen hier zusammen. Tragen beide Grenzen *dieselbe*
    // Beobachtung („etwa gleich alt wie auf dem Bild von 1968" klammert nach
    // beiden Seiten), steht sie einmal da. Kommen sie von Geburt und Tod
    // derselben Person, liest sich eine Lebensspanne besser als zwei Halbsätze.
    // Alles andere wird aufgezählt – auch wenn es dieselbe Person betrifft: eine
    // Altersstufe und ein Bildvergleich sind zwei Beobachtungen und gehören
    // beide genannt.
    const upperShown = latest && latest.kind !== 'today' ? latest : null;
    const samePerson = earliest !== null &&
        upperShown !== null &&
        earliest.personId !== null &&
        earliest.personId === upperShown.personId;
    const unique = [
        ...new Set([earliest?.label, upperShown?.label].filter((v) => Boolean(v))),
    ];
    const because = samePerson && earliest.kind === 'birth' && upperShown.kind === 'death'
        ? `${earliest.personName} lebte von ${earliest.year} bis ${upperShown.year}`
        : samePerson && earliest.kind === 'birth' && upperShown.kind === 'lifespan'
            ? `${earliest.personName} ist ${earliest.year} geboren, und mehr als ${ASSUMED_MAX_AGE} Jahre werden nicht angenommen`
            : unique.join(', ');
    const detail = impossible
        ? `Diese Personen können nicht gemeinsam auf einem Bild sein: ${because}. ` +
            'Eines der Jahre stimmt nicht – oder eine Markierung zeigt jemand anderen.'
        : `${span !== null
            ? `Das Bild ist zwischen ${from} und ${to} entstanden`
            : from !== null
                ? `Das Bild ist nach ${from} entstanden`
                : `Das Bild ist vor ${to} entstanden`}, denn ${because}.${missing.length > 0
            ? ` Bei ${missing.length === 1
                ? `${missing[0].displayName} steht kein Jahr`
                : `${missing.length} Personen auf dem Bild steht kein Jahr`} – mit Lebensdaten wird das Fenster enger.`
            : ''}`;
    return {
        from,
        to,
        span,
        earliest,
        latest,
        label,
        detail,
        date: estimateAsFuzzyDate(impossible ? null : from, impossible ? null : to),
        missing,
        used,
        appraised,
        appraisable,
        compared,
        judgementConflict,
        impossible,
    };
}
/**
 * Dasselbe aus der Sicht eines Bildes: Markierungen plus die Personen, die
 * ohne Rechteck verknüpft sind.
 *
 * Beide Quellen zusammen, weil beide dasselbe behaupten – „diese Person ist
 * auf diesem Bild". Dieselbe Lesart hat `photoDateConflicts`, und dass die
 * Schätzung genau die Menschen wägt, die auch bei den Widersprüchen zählen,
 * ist kein Zufall: es ist dieselbe Rechnung.
 */
export function estimatePhotoDateFor(regions, linked, opts = {}) {
    return estimatePhotoDate([
        // Markierungen zuerst: nur sie tragen Altersstufe und Vergleiche, und
        // beim Entdoppeln gewinnt die erste Nennung.
        ...regions.map((region) => region.person
            ? {
                person: region.person,
                apparentAge: region.apparentAge ?? null,
                comparisons: region.comparisons ?? [],
            }
            : null),
        ...linked.map((person) => ({ person })),
    ], opts);
}
export function compareToEstimate(stored, estimate) {
    const range = fuzzyDateYearRange(stored);
    // Offene Seiten als Unendlich rechnen – „nach 1955" ist ein Fenster, dessen
    // oberes Ende noch nicht gefunden ist, kein Fenster ohne oberes Ende.
    const storedFrom = range.from ?? Number.NEGATIVE_INFINITY;
    const storedTo = range.to ?? Number.POSITIVE_INFINITY;
    const estFrom = estimate.from ?? Number.NEGATIVE_INFINITY;
    const estTo = estimate.to ?? Number.POSITIVE_INFINITY;
    if (storedFrom === estFrom && storedTo === estTo)
        return 'match';
    if (estFrom >= storedFrom && estTo <= storedTo)
        return 'narrower';
    if (estFrom <= storedFrom && estTo >= storedTo)
        return 'wider';
    return 'shifted';
}
//# sourceMappingURL=photo-date-estimate.js.map